import React from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { ListLayout } from '../../global/tables/table-list-layout';
import TableLoadingRow from '../../global/tables/table-loading-row';
import { RoleListItem } from './_role-list-item/role-list-item';
import DefaultButton from '../../global/default-button/default-button';
import { IRolesDTO } from '../../../api/roles.dao';
import './roles-list.scss';
import { RolesVM } from '../roles.viewmodel';
import { DESCRIPTION, LOADING } from '../../../constants/common-labels.constants';

interface IProps {
    intl: IntlShape;
    vm: RolesVM;
}

const RolesList = injectIntl(mobxReact.observer((props: IProps) => {
    const { vm, intl } = props;
    const { rolesList, showCreateRoleForm, isLoading } = vm;

    const loadMoreButtonLabel = vm.isLoadingMore ?
        intl.formatMessage(
            LOADING,
            {
                suffix: '...'
            }
        ) : intl.formatMessage(
            {
                id: 'oe.common.loadMore',
                defaultMessage: 'Load More',
                description: 'Button label'
            }
        );

    const title = (
        intl.formatMessage(
            {
                id: 'oe.role.roles',
                defaultMessage: 'Roles',
                description: 'Section header'
            })
    );

    const thead = [
        intl.formatMessage(
            {
                id: 'oe.common.name',
                description: 'Label text',
                defaultMessage: 'Name',
            }),
        intl.formatMessage(DESCRIPTION),
    ];

    const isEmpty = !isLoading && !rolesList?.length;

    return (
        <div className='page-container'>
            <section className='top-page-title horizontal-row horizontal-row--with-bottom-margin'>
                <h1 className='text-ellipsis'>{title}</h1>
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        <DefaultButton
                            className='events-list__button-new'
                            modifier='fat'
                            text={intl.formatMessage(
                                {
                                    id: `oe.role.newRole`,
                                    description: 'New Role button',
                                    defaultMessage: '{prefix}New Role',
                                },
                                {
                                    prefix: '+ '
                                }
                            )}
                            onClick = {() => showCreateRoleForm(intl)}
                        />
                    </div>
                </div>
            </section>
            <section className='accounts-list fixed-list-layout'>
                <div>
                    <ListLayout thead={thead}>
                        { !isLoading && !isEmpty && rolesList.map((item: IRolesDTO) => {
                            return <RoleListItem
                                isRoleSelected={vm.isRoleFormShown(item.linkName + 'role')}
                                isPermissionSelected={vm.isRoleFormShown(item.linkName + 'permission')}
                                handlePermissionsClick={vm.handlePermissionsClick}
                                handleEditRoleClick={vm.editRole}
                                key={item.linkName}
                                {...item}
                            />;
                        })}
                        { isLoading && <TableLoadingRow colSpan={thead.length} />}
                    </ListLayout>
                    { isEmpty && (
                        <div className='empty-list-message'>
                            <FormattedMessage
                                description='Roles list empty text'
                                defaultMessage='There are no roles.'
                                id='oe.roles.emptyListText'
                            />
                        </div>
                    )}
                </div>
                {!!vm.loadMorePaginationToken &&
                    <div className='roles-list__load-more-wrapper'>
                        <DefaultButton
                            className='load-more-button'
                            disabled={vm.isLoadingMore}
                            onClick={vm.onLoadMore}
                            text={loadMoreButtonLabel}
                        />
                    </div>
                }
            </section>
        </div>
    );
}));

export default RolesList;
